/* eslint-disable react/prop-types -- project uses JSX without PropTypes package */
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import ReactTable from "../ReactTable";
import {
  formatPrimaryMeasurements,
  getChartDisplayName,
} from "../../helpers/functions";
import { TABLE_MEASUREMENTS } from "../../types/types";
import EditIcon from "../../assets/svg/icon_edit.svg?react";
import TrashIcon from "../../assets/svg/icon_trash.svg?react";

function SizePills({ labels }) {
  if (!labels?.length) {
    return <span className="text-sm text-slate-500">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {labels.map((name) => (
        <span
          key={name}
          className="inline-flex items-center rounded-full bg-slate-100 text-slate-800 px-2.5 py-0.5 text-xs font-medium"
        >
          {name}
        </span>
      ))}
    </div>
  );
}

export default function SizingChartsListTable({
  items,
  isLoading,
  unit = TABLE_MEASUREMENTS.CM,
  brands = [],
  chartMeta = {},
  onDeleteRow,
}) {
  const navigate = useNavigate();

  const brandNameById = useMemo(() => {
    const m = {};
    brands.forEach((b) => {
      m[b.id] = b.name;
    });
    return m;
  }, [brands]);

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper();
    return [
      columnHelper.accessor((row) => row.chartName || getChartDisplayName(row.id, chartMeta), {
        id: "chartName",
        header: () => <span>Chart Name</span>,
        cell: (info) => (
          <p className="font-medium">
            {info.getValue()}
          </p>
        ),
      }),
      columnHelper.accessor((row) => {
        const bid = row.brandId || chartMeta[row.id]?.brandId;
        return bid ? brandNameById[bid] ?? "—" : "—";
      }, {
        id: "brand",
        header: () => <span>Brand</span>,
        cell: (info) => (
          <p className="text-sm text-slate-600">{info.getValue()}</p>
        ),
      }),
      columnHelper.accessor((row) => row.gender || "—", {
        id: "gender",
        header: () => <span>Gender</span>,
        cell: (info) => (
          <p className="text-sm text-slate-600 capitalize">{info.getValue()}</p>
        ),
      }),
      columnHelper.accessor((row) => formatPrimaryMeasurements(row.sizes), {
        id: "measurements",
        header: () => <span>Measurements</span>,
        cell: (info) => (
          <p className="text-sm text-slate-600">{info.getValue()}</p>
        ),
      }),
      columnHelper.accessor((row) => row.sizes?.map((s) => s.name) ?? [], {
        id: "sizes",
        header: () => <span>Sizes</span>,
        cell: (info) => <SizePills labels={info.getValue()} />,
      }),
      columnHelper.display({
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: (info) => {
          const chart = info.row.original;
          const base = new URLSearchParams({
            defaultMeasurement: unit || TABLE_MEASUREMENTS.CM,
          });
          const editUrl = `/sizing-charts/${chart.id}?${base.toString()}`;

          return (
            <div className="flex items-center justify-end gap-1">
              <button
                type="button"
                title="Edit chart"
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-primary transition-colors"
                onClick={() => navigate(editUrl)}
              >
                <EditIcon className="w-5 h-5" aria-hidden />
              </button>
              <button
                type="button"
                title="Delete chart"
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-red-600 transition-colors"
                onClick={() => onDeleteRow(chart)}
              >
                <TrashIcon className="w-5 h-5" aria-hidden />
              </button>
            </div>
          );
        },
      }),
    ];
  }, [brandNameById, chartMeta, navigate, onDeleteRow, unit]);

  return (
    <ReactTable
      columns={columns}
      data={items || []}
      isLoading={isLoading}
      totalPages={1}
      emptyMessage="No charts in this list."
    />
  );
}
